import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
} from 'chart.js';
import { Radar, Bar } from 'react-chartjs-2';
import { useCurrentTheme } from '../../../../hooks/useCurrentTheme';
import {
  createCenterCirclePlugins,
  formatTopicRadarLabels,
  topicRadarOptions,
  createLabelHoverTooltipPlugin,
} from '../../../../utils/topicRadar';
import { TopicRadarData } from '../../../../types/Widgets';

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

declare module 'chart.js' {
  interface Scale {
    xCenter?: number;
    yCenter?: number;
    drawingArea: number;
  }
}

interface TopicRadarChartProps {
  data: TopicRadarData | null | undefined;
  /** The radar tooltip's text for a value; defaults to "Performance: 72%". */
  formatTooltipValue?: (value: number) => string;
}

// Presentational radar/bar chart for topic performance. Renders a radar for 3+
// topics and falls back to a bar chart for 1-2 topics. The data source (current
// user, a faculty-scoped student, or a cohort aggregate) is the caller's
// concern; this component only draws what it's handed.
const TopicRadarChart: React.FC<TopicRadarChartProps> = ({
  data,
  formatTooltipValue,
}) => {
  const radarChartRef = useRef<ChartJS<'radar'> | null>(null);
  const barChartRef = useRef<ChartJS<'bar'> | null>(null);
  const { isDarkMode } = useCurrentTheme();

  // Update plugins when theme changes
  useEffect(() => {
    // Unregister existing plugins
    const plugins = createCenterCirclePlugins(isDarkMode);
    const labelHoverPlugin = createLabelHoverTooltipPlugin();

    Object.values(plugins).forEach((plugin) => {
      ChartJS.unregister(plugin as Plugin);
      ChartJS.register(plugin);
    });

    // Ensure label-hover tooltip plugin is registered (safe to unregister first)
    ChartJS.unregister(labelHoverPlugin);
    ChartJS.register(labelHoverPlugin);

    // Force chart update
    if (radarChartRef.current) {
      radarChartRef.current.update();
    }
  }, [isDarkMode]);

  useEffect(() => {
    const handleResize = () => {
      if (radarChartRef.current) {
        radarChartRef.current.resize();
      }
      if (barChartRef.current) {
        barChartRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const reducedTopicPercentage = data
    ? Object.keys(data).reduce(
        (acc, key) => {
          acc[key] = data[key].percentage;
          return acc;
        },
        {} as { [key: string]: number }
      )
    : {};

  const filteredTopicLabels = Object.keys(reducedTopicPercentage).map((label) =>
    formatTopicRadarLabels(label)
  );

  const filteredTopicData = Object.values(reducedTopicPercentage);

  const chartData = {
    labels: filteredTopicLabels,
    datasets: [
      {
        data: filteredTopicData,
        fill: true,
        backgroundColor: isDarkMode
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(99, 133, 254, 0.1)',
        borderColor: isDarkMode ? '#FEFEFE' : '#5a68af',
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
        pointHoverBackgroundColor: 'transparent',
        pointHoverBorderColor: 'transparent',
        borderRadius: 8,
        barPercentage: filteredTopicLabels.length === 1 ? 0.5 : 0.8,
      },
    ],
  };

  const barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        bodyFont: {
          family: 'var(--font-family-base)',
          size: 12,
        },
        padding: 12,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.raw as number;
            return `Performance: ${value.toFixed(0)}%`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'var(--font-family-base)',
          },
          callback: function (value) {
            return `${value}%`;
          },
        },
        border: {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDarkMode ? '#FEFEFE' : '#111215',
          font: {
            family: 'var(--font-family-base)',
          },
        },
        border: {
          color: 'rgba(255, 255, 255, 0.3)',
        },
        offset: filteredTopicLabels.length === 1,
      },
    },
  };

  if (filteredTopicLabels.length === 0) {
    return null;
  }

  return filteredTopicLabels.length <= 2 ? (
    <Bar ref={barChartRef} data={chartData} options={barChartOptions} />
  ) : (
    <Radar
      ref={radarChartRef}
      data={chartData}
      options={topicRadarOptions(isDarkMode, formatTooltipValue)}
    />
  );
};

export default TopicRadarChart;
